import { useDispatch } from 'react-redux';
import { enqueueNotification, enqueueNotificationMultiple } from 'store/modules/ui';

/**
 * Build the options object for a notification, including an optional auto-dismiss duration.
 * Pass durationSeconds to force the toast to dismiss after that many seconds. Omit to use
 * the app-wide default (notistack's 5s).
 */
const buildOptions = (variant: string, durationSeconds?: number) => ({
  variant,
  ...(durationSeconds !== undefined && { autoHideDuration: durationSeconds * 1000 }),
});

export const useNotifications = () => {
  const dispatch = useDispatch();

  const showSuccessNotification = (message: string, durationSeconds?: number) => {
    dispatch(
      enqueueNotification({
        message,
        options: buildOptions('success', durationSeconds),
      })
    );
  };

  const showErrorNotification = (message: string, durationSeconds?: number) => {
    dispatch(
      enqueueNotification({
        message,
        options: buildOptions('error', durationSeconds),
      })
    );
  };

  const showInfoNotification = (message: string, durationSeconds?: number) => {
    dispatch(
      enqueueNotification({
        message,
        options: buildOptions('info', durationSeconds),
      })
    );
  };

  const showWarningNotification = (message: string, durationSeconds?: number) => {
    dispatch(
      enqueueNotification({
        message,
        options: buildOptions('warning', durationSeconds),
      })
    );
  };

  const showMultipleNotifications = (
    notifs: { message: string; variant: string; durationSeconds?: number }[]
  ) => {
    dispatch(
      enqueueNotificationMultiple(
        notifs.map(n => ({
          message: n.message,
          options: buildOptions(n.variant, n.durationSeconds),
        }))
      )
    );
  };

  return {
    showSuccessNotification,
    showErrorNotification,
    showInfoNotification,
    showWarningNotification,
    showMultipleNotifications,
  };
};
